/** 铃声配置 */
type BellConstructorConfig = {
    /** 铃声的唯一标识 */
    key: string;
    /** 铃声音频文件地址 */
    resourceUrl: string;
    /** 铃声优先级，数字越小，优先级越高 */
    priority: number;
    /** 单次播放重复次数，默认1次，0次表示不播放 */
    loopTimes?: number;
    /** 铃声描述信息 */
    desc?: string;
    /** 音量：取值 0~1 */
    volume?: number;
    /**
     * 铃声是否可以在播放到一半时被中断(例如有更高优先级铃声)
     * 默认为 true，如无特殊业务诉求，请勿设置为true
     */
    interruptable?: boolean;
};

/** 铃声配置 */
type BellConfig = Readonly<Required<BellConstructorConfig>>;

/**
 * 🔔 铃声
 */
class Bell {
    private readonly config: BellConfig;

    private readonly audioEl: HTMLAudioElement;

    private playWhenResourceReady: boolean = false;

    private waitResourceLoading: Promise<any>;

    /** 单次播放内循环次数计数 */
    private loopCount = 0;

    constructor(options: BellConstructorConfig) {
        this.config = {
            loopTimes: 1,
            desc: '',
            volume: 1,
            interruptable: true,
            ...options,
        };

        // 预加载音频资源
        this.audioEl = new Audio();
        this.audioEl.preload = 'auto';
        this.audioEl.volume = this.config.volume;

        this.waitResourceLoading = new Promise(resolve => {
            // 监听资源加载完成的回调
            this.audioEl.addEventListener(
                'canplaythrough',
                () => {
                    resolve();
                },
                false,
            );
        });
    }

    isPalying() {
        return this.playWhenResourceReady;
    }

    get key() {
        return this.config.key;
    }

    get priority() {
        return this.config.priority;
    }

    /** 播放铃声 */
    play() {
        if (this.config.loopTimes === 0) {
            return;
        }

        // 当前铃声已经在播放中，无需再次播放
        if (this.playWhenResourceReady) {
            return;
        }

        // 铃声播放时资源尚未加载成功
        if (this.audioEl.src === '') {
            this.audioEl.src = this.config.resourceUrl;
        }

        this.playWhenResourceReady = true;

        this.waitResourceLoading.then(() => {
            if (this.playWhenResourceReady) {
                this.loopCount = 0;
                this.audioEl.addEventListener('pause', this.pauseHanler, false);
                const playPromise = this.audioEl.play();
                // 受浏览器自动播放限制，某些场景会触发铃声播放执行失败，此时应该中断播放
                if (playPromise !== undefined) {
                    // 兼容低版本浏览器，只有返回promise的情况，才做异常处理
                    playPromise.catch(() => {
                        this.stop();
                    });
                }
            }
        });
    }

    /** 中断播放 */
    stop() {
        this.playWhenResourceReady = false;
        if (this.config.interruptable) {
            this.loopCount = 0;

            this.audioEl.removeEventListener('pause', this.pauseHanler, false);
            this.audioEl.pause();
            this.audioEl.currentTime = 0; // 重置播放进度，下次播放直接从头播放
        }
    }

    /** 音频播放结束后的回调 */
    private pauseHanler = () => {
        this.loopCount += 1;
        if (this.playWhenResourceReady) {
            if (this.loopCount < this.config.loopTimes) {
                this.audioEl.play();
            } else {
                // 播放够数了，结束
                this.stop();
            }
        }
    };
}

export default Bell;
export { BellConstructorConfig };
